import React, { useEffect, useRef } from "react";

/**
 * Draggable-pin OpenStreetMap picker.
 *
 * Extracted verbatim from AddressDialog so the apartment form can reuse it
 * instead of carrying a second copy of this lifecycle — including the
 * stacking-context fix below, which is easy to lose in a re-implementation.
 *
 * Props:
 *   lat, lng   — current pin position (controlled)
 *   onChange   — (lat, lng) after a drag or a tap on the map
 *   className  — override the default height/border
 *   testId     — data-testid on the container
 */
export default function LeafletMap({
  lat, lng, onChange,
  className = "relative z-0 w-full h-64 rounded-2xl overflow-hidden border border-neutral-200",
  testId = "address-map",
}) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  // Keep the latest onChange without re-running the init effect — the map is
  // deliberately built once (see the empty dep array below), so capturing a
  // stale closure here would silently stop reporting pin moves.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Lazy-load Leaflet so the bundle stays small on cold-start
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");
      if (cancelled || !ref.current) return;
      // Default marker icons need URLs (Webpack quirk)
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
      });
      const map = L.map(ref.current, { zoomControl: true }).setView([lat, lng], 15);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap",
        maxZoom: 19,
      }).addTo(map);
      const marker = L.marker([lat, lng], { draggable: true }).addTo(map);
      marker.on("dragend", () => {
        const p = marker.getLatLng();
        onChangeRef.current?.(p.lat, p.lng);
      });
      map.on("click", (e) => {
        marker.setLatLng(e.latlng);
        onChangeRef.current?.(e.latlng.lat, e.latlng.lng);
      });
      mapRef.current = map;
      markerRef.current = marker;
    })();
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);   // intentionally only init once

  // Recenter when lat/lng change from outside
  useEffect(() => {
    if (mapRef.current && markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
      mapRef.current.setView([lat, lng], mapRef.current.getZoom());
    }
  }, [lat, lng]);

  // relative + z-0: Leaflet's own CSS sets position:relative on this div
  // (.leaflet-container) but no z-index, so without one here it never gets
  // its own stacking context — its internal panes (tiles/markers, z-index
  // up to 700 in leaflet.css) leak out and paint over sibling UI with a
  // much lower z-index, like a search-suggestions dropdown above it.
  return <div ref={ref} className={className} data-testid={testId}/>;
}
